import { alpha, darken, getContrastRatio } from "@mui/material/styles";
import themeFile from "../theme.json";

const COLOR_VALUE_PATTERN =
  /^(#(?:[\da-fA-F]{3,8})|rgba?\([^)]*\)|hsla?\([^)]*\)|oklch\([^)]*\)|oklab\([^)]*\)|lab\([^)]*\)|lch\([^)]*\)|hwb\([^)]*\)|color\([^)]*\))$/;

const COLOR_KEYS = {
  primary: ["primary", "primaryColor", "primary-color", "primary_color", "brand-primary", "brandPrimary"],
  secondary: ["secondary", "secondaryColor", "secondary-color", "secondary_color", "brand-secondary", "brandSecondary"],
  accent: ["accent", "accentColor", "accent-color", "accent_color", "brand-accent", "brandAccent"],
  background: ["background", "backgroundColor", "background-color", "background_color", "page-background"],
  surface: ["surface", "surfaceColor", "surface-color", "surface_color", "card-background"],
  text: ["text", "textColor", "text-color", "text_color", "foreground", "foregroundColor"],
};

const isColorValue = (value) => typeof value === "string" && COLOR_VALUE_PATTERN.test(value.trim());

const getFirstColor = (appVars, keys, fallback) => {
  if (!appVars) {
    return fallback;
  }

  for (const key of keys) {
    if (isColorValue(appVars[key])) {
      return appVars[key].trim();
    }
  }

  return fallback;
};

export const getAppAppearance = (appVars) => {
  const primary = getFirstColor(appVars, COLOR_KEYS.primary, themeFile.palette.primary.dark);
  const secondary = getFirstColor(appVars, COLOR_KEYS.secondary, themeFile.palette.primary.light);
  const accent = getFirstColor(appVars, COLOR_KEYS.accent, themeFile.palette.secondary.dark);
  const pageBase = getFirstColor(appVars, COLOR_KEYS.background, "#f5fbff");
  const surface = getFirstColor(appVars, COLOR_KEYS.surface, "#ffffff");
  const textPrimary = getFirstColor(appVars, COLOR_KEYS.text, "#10243c");
  const heroText = getFirstColor(appVars, COLOR_KEYS.text, "#10243c");
  const codeBase = darken(primary, 0.12);

  return {
    colors: {
      primary,
      secondary,
      accent,
      surface,
      pageBase,
      textPrimary,
      textSecondary: alpha(textPrimary, 0.76),
      textMuted: alpha(textPrimary, 0.58),
      heroText,
      divider: alpha(primary, 0.1),
      border: alpha(primary, 0.13),
      borderStrong: alpha(primary, 0.18),
      surfaceSoft: alpha("#ffffff", 0.7),
      surfaceStrong: alpha("#ffffff", 0.84),
      gridLine: alpha(primary, 0.07),
      heroWash: `linear-gradient(135deg, ${alpha(primary, 0.18)} 0%, ${alpha(secondary, 0.18)} 48%, ${alpha(accent, 0.12)} 100%)`,
      pageBackground: `radial-gradient(circle at top left, ${alpha(primary, 0.18)} 0%, transparent 34%), radial-gradient(circle at top right, ${alpha(secondary, 0.18)} 0%, transparent 28%), radial-gradient(circle at bottom center, ${alpha(accent, 0.12)} 0%, transparent 30%), linear-gradient(140deg, ${pageBase} 0%, ${alpha(secondary, 0.05)} 48%, #ffffff 100%)`,
      codeBackground: `linear-gradient(180deg, ${alpha(codeBase, 0.94)} 0%, ${alpha(darken(codeBase, 0.12), 0.98)} 100%)`,
      codeBorder: alpha("#ffffff", 0.14),
      codeText: alpha("#ffffff", 0.92),
      success: themeFile.palette.feedback.success.background,
      danger: themeFile.palette.feedback.error.background,
    },
    shadows: {
      shell: `0 32px 90px ${alpha(primary, 0.14)}`,
      panel: `0 18px 48px ${alpha(primary, 0.1)}`,
      raised: `0 14px 36px ${alpha(primary, 0.12)}`,
    },
  };
};
